import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Text, Link } from "@sitecore-jss/sitecore-jss-react";
import { useWindowSize } from "react-use";
import { LinkField } from "@sitecore-jss/sitecore-jss-react/types/components/Link";
import {
  HeaderMenuSection,
  Language,
  PrimaryHeaderMenuLink,
} from "../../types/header.types";
import { useMemorySafeTimeout } from "../../../../hooks/useMemorySafeTimeout";
import "./MobileMenu.scss";

import HamburgerButton, {
  HamburgerButtonProps,
} from "../HamburgerButton/HamburgerButton";
import StockTicker from "../StockTicker/StockTicker";
import LanguageSwitch from "../LanguageSwitch/LanguageSwitch";
import Search, { SearchProps } from "../Search/Search";
import { TLinkField } from "../../../../types/sitecore.types";
import RoutableNotEditableLink from "../../../../react-only-components/RoutableNotEditableLink";

interface MobileMenuProps extends HamburgerButtonProps, SearchProps {
  content: PrimaryHeaderMenuLink[];
  languages: Language[];
  stockTickerLink: TLinkField;
}

const MobileMenu: FC<MobileMenuProps> = memo(
  ({ isOpen, content, languages, searchPage, hideSearch, searchLabel, clickHandler, stockTickerLink }) => {
    const { height, width } = useWindowSize();
    const safeTimeout = useMemorySafeTimeout();

    const [activeLevel, setActiveLevel] = useState<string>("");
    const [showSecondLevel, setShowSecondLevel] = useState<boolean>(false);

    const handleLevelClick: (level: string) => void = useCallback((level) => {
      setActiveLevel(level);
      if (level.length === 0) return;
      setShowSecondLevel(true);
    }, []);

    const handleSecondLevelClose = useCallback(() => {
      setShowSecondLevel(false);
      setTimeout(() => {
        setActiveLevel("");
      }, 220);
    }, []);

    const isLink: (link: LinkField) => boolean = useCallback((link) => {
      return (link.value.href?.length ?? 0) > 0;
    }, []);

    useEffect(() => {
      if (typeof window !== 'undefined' && 'customElements' in window) {
        import('@patternfly/pfe-icon-onhighway/dist/pfe-icon.js');
      }
    }, []);

    const NavigationLevel: FC<{
      isFirst?: boolean;
      isVisible: boolean;
      primaryData?: PrimaryHeaderMenuLink[];
      secondaryData?: HeaderMenuSection[];
      onLevelClick?: (level: string) => void;
      onSecondLevelClose?: () => void;
    }> = useMemo(() => {
      return function component({
        isFirst = false,
        isVisible,
        primaryData = [],
        secondaryData = [],
        onLevelClick = () => {},
        onSecondLevelClose = () => {},
      }) {
        return (
          <div
            className={`MobileMenu__NavigationLevel ${
              isFirst ? "" : "MobileMenu__NavigationLevel--second"
            }`}
            style={{
              transform: isVisible
                ? "translateX(0)"
                : `translateX(${isFirst ? "-100%" : "+100%"})`,
            }}
          >
            {isFirst ? (
              primaryData.map(({ fields: navItem }, i) =>
                isLink(navItem.link) ? (
                  <Link field={navItem.link} key={i} />
                ) : (
                  <button
                    onClick={() => onLevelClick(navItem.menuButton.value)}
                    key={i}
                  >
                    <Text field={navItem.menuButton} />
                    <pfe-icon
                      icon="cnh-arrow-forward"
                      size={"24"}
                      color={"black"}
                    />
                  </button>
                )
              )
            ) : (
              <>
                <button
                  onClick={onSecondLevelClose}
                  className="MobileMenu__NavigationLevel_back"
                >
                  <pfe-icon
                    icon="cnh-arrow-forward"
                    size={"24"}
                    color={"black"}
                  />
                  Back
                </button>
                {secondaryData.map(({ fields: { link } }, i) => (
                  <Link field={link} key={i} />
                ))}
              </>
            )}
          </div>
        );
      };
    }, [width]);

    // The following is to prevent the in-level animation
    // from occuring when closing the drawer

    const [hideFirstLevelAnimation, setHideFirstLevelAnimation] =
      useState<boolean>(false);
    const tid = useRef<NodeJS.Timeout>();

    useEffect(() => {
      if (!isOpen && activeLevel !== "") {
        setHideFirstLevelAnimation(true);
        tid.current = safeTimeout(() => {
          setActiveLevel("");
          setShowSecondLevel(false);
          setHideFirstLevelAnimation(false);
        }, 300);
      } else {
        if (activeLevel !== "") {
          setActiveLevel("");
          setShowSecondLevel(false);
          setHideFirstLevelAnimation(false);
        }
        if (!tid.current) return;
        clearTimeout(tid.current);
      }
    }, [isOpen]);

    return (
      <>
        <HamburgerButton isOpen={isOpen} clickHandler={clickHandler} />

        <div
          className="MobileMenu"
          style={{
            height: height - 67,
            transform: isOpen ? "translateX(0)" : "translateX(100%)",
          }}
        >
          <div>
            {!hideFirstLevelAnimation && (
              <NavigationLevel
                isFirst
                isVisible={!showSecondLevel}
                onLevelClick={handleLevelClick}
                primaryData={content}
              />
            )}

            <NavigationLevel
              isVisible={showSecondLevel}
              onSecondLevelClose={handleSecondLevelClose}
              secondaryData={
                content.find(
                  ({
                    fields: {
                      menuButton: { value: c },
                    },
                  }) => c === activeLevel
                )?.fields?.section1
              }
            />
          </div>

          <div>
            {stockTickerLink?.value?.href ? (
              <RoutableNotEditableLink href={stockTickerLink.value.href} target={stockTickerLink.value.target} className="MobileMenu__stockticker_container">
                <StockTicker/>
              </RoutableNotEditableLink>
            ) : (
              <div className="MobileMenu__stockticker_container">
                  <StockTicker/>
              </div>
            )}

            <div className="MobileMenu__langswitch_container">
              <Search searchPage={searchPage} hideSearch={hideSearch} searchLabel={searchLabel}/>
              <LanguageSwitch languages={languages} />
            </div>
          </div>
        </div>
      </>
    );
  }
);

export default MobileMenu;
